import SelectTeam from "@components/account/Teams/SelectTeam";
import {
  faChevronDown,
  faChevronUp,
  faExternalLinkAlt,
  faSignOutAlt,
  faUserPlus,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  usePreventRecordingInterruptions,
  useAuth,
  useTeam,
  useCurrentUser,
  useOrganization,
} from "@hooks";
import { useDisplayName } from "@hooks/use-display-name";
import { useSpring, animated, config } from "@react-spring/web";
import { useFlags } from "launchdarkly-react-client-sdk";
import mixpanel from "mixpanel-browser";
import { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router";

import { RandomUserImage } from "../../common/Randomizer";

function MenuItem({
  onClick,
  title,
  icon = null,
  dataTestId = null,
}) {
  return (
    <div
      className="block px-4 py-2 text-sm text-primary hover:bg-tertiary cursor-pointer"
      role="menuitem"
      onClick={onClick}
      data-testid={dataTestId}
    >
      {icon && <FontAwesomeIcon icon={icon} />} {title}
    </div>
  );
}

const AuthNav = ({ show, shouldShowTeamDropdown = true }) => {
  const [shouldShowMenu, setShouldShowMenu] = useState(false);
  const { isOrgAdmin } = useOrganization();
  const { isUserAuthenticated, logout } = useAuth();
  const { user } = useCurrentUser();
  const { currentTeam, setCurrentTeam, userTeams } = useTeam();
  const { useLegacyNotes } = useFlags();
  const { handleActionWithPrevention } =
    usePreventRecordingInterruptions();
  const menuAnim = useSpring({
    config: config.wobbly,
    opacity: shouldShowMenu ? 1 : 0,
    transform: shouldShowMenu ? "scale(1)" : "scale(0.8)",
  });
  const displayName = useDisplayName(user);

  useEffect(() => {
    if (!currentTeam?.uuid && userTeams?.length) {
      setCurrentTeam(userTeams[0]);
    }
  }, [currentTeam, userTeams, setCurrentTeam]);

  /**
   * Hook that alerts clicks outside of the passed ref
   */
  function useOutsideAlerter(ref) {
    useEffect(() => {
      /**
       * Alert if clicked on outside of element
       */
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShouldShowMenu(false);
        }
      }

      // Bind the event listener
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        // Unbind the event listener on clean up
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [ref]);
  }

  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef);
  const navigate = useNavigate();

  const menuClassName = shouldShowMenu
    ? "origin-top-right absolute right-0 mt-3 w-48 rounded-standard shadow-lg bg-secondary divide-y divide-secondary z-50"
    : "hidden";

  const menuItemOnClick = (path) => {
    handleActionWithPrevention(() => {
      setShouldShowMenu(false);
      navigate(path);
    });
  };

  // if user authenticated, then we can show their username in the navbar.
  if (isUserAuthenticated) {
    return (
      <div
        className="relative inline-block text-left md:pl-3"
        ref={wrapperRef}
      >
        <div className="flex flex-row gap-2 items-center">
          {shouldShowTeamDropdown && (
            <SelectTeam
              currentTeam={currentTeam}
              setCurrentTeam={setCurrentTeam}
            />
          )}
          <div className="flex items-center">
            <button
              className="focus:outline-hidden"
              type="button"
              id="options-menu"
              data-testid="auth-nav-button"
              onClick={() => setShouldShowMenu(!shouldShowMenu)}
            >
              <div className="group flex flex-row items-center hover:bg-secondary py-1 px-2 rounded-lg transition-all">
                <h4 className="text-primary group-hover:text-brand transition-all flex flex-row items-center space-x-2">
                  <FontAwesomeIcon
                    icon={
                      shouldShowMenu ? faChevronUp : faChevronDown
                    }
                    size="xs"
                    className="text-secondary"
                  />
                  <span
                    className="hidden md:block"
                    data-testid="navUserName"
                  >
                    {displayName}
                  </span>
                </h4>
                <div className="inline-flex justify-center rounded-full shadow-xs ml-2 h-10 w-10 bg-gray-200 focus:outline-hidden">
                  <RandomUserImage />
                </div>
              </div>
            </button>
          </div>
        </div>

        <animated.div
          style={menuAnim}
          className={menuClassName}
          role="menu"
        >
          <div className="py-1">
            <MenuItem
              onClick={() => menuItemOnClick("/account")}
              title="Account"
            />
            {(!userTeams?.filter(
              (team) => team.orgAllowsDirectMemberAdd,
            )?.length ||
              isOrgAdmin) && (
              <MenuItem
                onClick={() => {
                  menuItemOnClick("/teams");
                }}
                title={
                  !currentTeam?.uuid ? (
                    <span className="text-brand">
                      Invite My Team{" "}
                      <FontAwesomeIcon icon={faUserPlus} />
                    </span>
                  ) : userTeams?.length > 1 ? (
                    "My Teams"
                  ) : (
                    "My Team"
                  )
                }
              />
            )}

            <MenuItem
              onClick={() => menuItemOnClick("/dashboard")}
              title="Dashboard"
            />
            <MenuItem
              onClick={() => menuItemOnClick("/inbox")}
              title="Inbox"
            />
            <MenuItem
              onClick={() => menuItemOnClick("/templates")}
              title="Adaptive Templates"
            />
          </div>
          <div className="py-1">
            <MenuItem
              onClick={() => menuItemOnClick("/notebook")}
              title="Patient Notebook"
            />
            {useLegacyNotes && (
              <>
                <MenuItem
                  onClick={() => menuItemOnClick("/new_note/select")}
                  title="New Typed Note"
                />
                <MenuItem
                  onClick={() => menuItemOnClick("/typed_templates")}
                  title="Typed Note Templates"
                  dataTestId="typed-note-nav"
                />
              </>
            )}
          </div>
          <div className="py-1">
            <div
              className="block px-4 py-2 text-sm text-primary hover:bg-secondary cursor-pointer"
              role="menuitem"
              onClick={() => {
                setShouldShowMenu(false);
                mixpanel.track("What's New Clicked - Navbar");
                const newWindow = window.open(
                  "https://scribenote.com/whats-new",
                  "_blank",
                  "noopener,noreferrer",
                );
                if (newWindow) {
                  newWindow.opener = null;
                }
              }}
            >
              What&apos;s New{" "}
              <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
            </div>
            <div
              className="block px-4 py-2 text-sm text-primary hover:bg-secondary cursor-pointer"
              role="menuitem"
              onClick={() => {
                setShouldShowMenu(false);
                const newWindow = window.open(
                  "https://docs.scribenote.com/",
                  "_blank",
                  "noopener,noreferrer",
                );
                if (newWindow) {
                  newWindow.opener = null;
                }
              }}
            >
              Help Docs{" "}
              <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
            </div>
            <div
              className="block px-4 py-2 text-sm text-primary hover:bg-secondary cursor-pointer"
              role="menuitem"
              onClick={() => {
                show(); // show the intercom messenger
              }}
            >
              Support{" "}
              <FontAwesomeIcon icon={faExternalLinkAlt} size="xs" />
            </div>
          </div>
          <div className="py-1">
            <MenuItem
              onClick={async () => {
                setShouldShowMenu(false);
                await logout();
                mixpanel.track("Logged Out");
              }}
              title="Log out"
              icon={faSignOutAlt}
            />
          </div>
        </animated.div>
      </div>
    );
  } else {
    return null;
  }
};

export default AuthNav;
